

import React from 'react';
import { Link } from 'react-router-dom';
import usePageMeta from '@/hooks/usePageMeta';
import { SEO_ROUTES } from '@/src/seo/routes';
import MarketingHeader from '@/components/marketing/MarketingHeader';
import MarketingFooter from '@/components/marketing/MarketingFooter';
import { ENGAGEMENT, ENGAGEMENT_MULTIPLE, PROOF_STRIP } from '@/components/marketing/siteFacts';
import {
  ArrowRight,
  ArrowLeft,
  Heart,
  Users,
  Globe,
  Target,
  Shield,
  Zap,
  Building2,
  MessageSquare
} from 'lucide-react';

/**
 * About Page
 * Company mission, story, and values
 */
export default function AboutPage() {
  usePageMeta(SEO_ROUTES['/company/about']);

  const currentYear = new Date().getFullYear();

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Header */}
      <MarketingHeader />

      {/* Hero Section */}
      <section className="bg-paper border-b border-v2border py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="text-4xl md:text-5xl font-bold text-ink mb-6">
              We built the Plaid before Plaid existed.<br />
              <span className="text-v2accent">Now we are building it for the safety net.</span>
            </h1>
            <p className="text-xl text-ink-500">
              In 2010 we connected commercial health insurance: member-consented claims from every major insurer, behind one
              integration. WeaveIn is the same primitive for government benefits &mdash; every funder&rsquo;s interface,
              from eligibility to claims to remittance, behind one ledger.
            </p>
          </div>
        </div>
      </section>

      {/* Founder story */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <h2 className="text-3xl font-bold text-ink dark:text-white mb-6">Why we are building it</h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 mb-6">
              Every plan, county and grant defines its own interface: how members are assigned, what data comes back, how
              services are authorized, submitted and paid. The community organization absorbs the difference by hand, and the
              smallest, most trusted organizations feel it first. In an illustrative ECM member-month, the interface takes $135
              of a $175 margin.
            </p>
            <p className="text-lg text-gray-600 dark:text-gray-300 mb-6">
              The lesson from 2010 was that the data primitive works, and that the money has to move through you first. So
              WeaveIn is payments-first: it runs the ledger the funds flow through, not a dashboard beside it.
            </p>
            <p className="text-lg text-gray-600 dark:text-gray-300">
              <strong className="text-ink dark:text-white">Our mission:</strong> shorten the path between people at
              risk and the care and support they are already entitled to &mdash; and make sure the people who deliver it get
              paid.
            </p>
          </div>
          <div className="rounded-2xl border border-gray-200 dark:border-gray-700 overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th className="p-4 text-left text-gray-500 dark:text-gray-400 font-semibold" />
                  <th className="p-4 text-left text-gray-500 dark:text-gray-400 font-semibold">2010 &middot; HealthExpense</th>
                  <th className="p-4 text-left text-ink font-semibold">2026 &middot; WeaveIn</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 dark:divide-gray-800">
                {[
                  ['Market', 'Commercial health insurance', 'Medicaid, Medicare and government benefits'],
                  ['Primitive', 'Member-consented claims from every major insurer', 'Every funder\u2019s interface: eligibility, claims, remittance'],
                  ['Proof', '$2.5B claims \u00b7 2,500+ employers \u00b7 6M+ eligible employees', '100+ CBOs \u00b7 955.6K-member cohort \u00b7 $3M+ earned by CBOs'],
                  ['Surface', 'Portal', 'API and an agent embedded in partner products'],
                  ['Revenue', 'Waited on account linking', 'Payments-first: a take on the funds that move'],
                ].map(([k, then, now]) => (
                  <tr key={k}>
                    <td className="p-4 font-semibold text-gray-500 dark:text-gray-400 uppercase text-xs tracking-wider">{k}</td>
                    <td className="p-4 text-gray-600 dark:text-gray-300">{then}</td>
                    <td className="p-4 text-gray-900 dark:text-gray-100 font-medium">{now}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="p-4 text-xs text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-800">
              $38B+ a year in U.S. social-care funding moves through these interfaces. Not a California problem.
            </p>
          </div>
        </div>
      </section>

      {/* Our Approach */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-ink dark:text-white mb-4">
              Our Approach: Digitizing Trust
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              We don't push healthcare messages from institutions. We leverage the trust that
              community organizations have built over decades.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white dark:bg-gray-900 rounded-xl p-8 shadow-lg">
              <div className="w-14 h-14 bg-v2accent rounded-xl flex items-center justify-center mb-6">
                <Target className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-ink dark:text-white mb-3">Seek</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Community organizations send trusted SMS messages in members' preferred languages.
                We meet people where they are—no app downloads required.
              </p>
            </div>

            <div className="bg-white dark:bg-gray-900 rounded-xl p-8 shadow-lg">
              <div className="w-14 h-14 bg-v2accent rounded-xl flex items-center justify-center mb-6">
                <Users className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-ink dark:text-white mb-3">Support</h3>
              <p className="text-gray-600 dark:text-gray-300">
                Multiple care partners integrate into a unified platform, reducing service
                fragmentation. One platform connects housing, food, healthcare, and more.
              </p>
            </div>

            <div className="bg-white dark:bg-gray-900 rounded-xl p-8 shadow-lg">
              <div className="w-14 h-14 bg-v2accent rounded-xl flex items-center justify-center mb-6">
                <Zap className="w-7 h-7 text-white" />
              </div>
              <h3 className="text-xl font-bold text-ink dark:text-white mb-3">Scale</h3>
              <p className="text-gray-600 dark:text-gray-300">
                AI-powered personalization ensures the right programs reach the right people.
                We track outcomes so you can see what works.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-ink dark:text-white mb-4">
              Our Values
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-v2accent-soft rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="w-8 h-8 text-v2accent" />
              </div>
              <h3 className="text-lg font-bold text-ink dark:text-white mb-2">Community First</h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                We build with and for communities, not on top of them.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-v2accent-soft rounded-full flex items-center justify-center mx-auto mb-4">
                <Shield className="w-8 h-8 text-v2accent" />
              </div>
              <h3 className="text-lg font-bold text-ink dark:text-white mb-2">Trust & Privacy</h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                We protect member data with enterprise-grade security and transparent practices.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-v2accent-soft rounded-full flex items-center justify-center mx-auto mb-4">
                <Globe className="w-8 h-8 text-v2accent" />
              </div>
              <h3 className="text-lg font-bold text-ink dark:text-white mb-2">Equity & Access</h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                We design for the underserved—110+ languages, SMS-first, no barriers.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-v2accent-soft rounded-full flex items-center justify-center mx-auto mb-4">
                <MessageSquare className="w-8 h-8 text-v2accent" />
              </div>
              <h3 className="text-lg font-bold text-ink dark:text-white mb-2">Outcomes Matter</h3>
              <p className="text-gray-600 dark:text-gray-300 text-sm">
                We measure what matters—not just engagement, but real health outcomes.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Platform */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="order-2 lg:order-1">
              <div className="bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-lg">
                <h3 className="text-xl font-bold text-ink dark:text-white mb-6">
                  By the Numbers
                </h3>
                <div className="grid grid-cols-2 gap-6">
                  {[ENGAGEMENT, ENGAGEMENT_MULTIPLE, ...PROOF_STRIP.slice(0, 4)].map((m) => (
                    <div key={m.label} title={m.definition}>
                      <div className="text-3xl font-bold text-ink dark:text-white">{m.value}</div>
                      <div className="text-gray-600 dark:text-gray-400 text-sm">{m.label}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            <div className="order-1 lg:order-2">
              <h2 className="text-3xl font-bold text-ink dark:text-white mb-6">
                Powered by Trust
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-6">
                WeaveIn.ai is built on the Trust platform—a micro-engagement platform that has
                been empowering community-based organizations, nonprofits, and faith-based
                organizations to connect underserved populations to critical services since 2018.
              </p>
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-6">
                Trust is the relationship layer: the community staff members already know,
                reaching them in their own language. WeaveIn is the ledger underneath, so the
                work those staff do is attributed to the funder that pays for it.
              </p>
              <Link
                to="/company/team"
                className="inline-flex items-center text-v2accent font-semibold hover:underline"
              >
                Meet Our Team
                <ArrowRight className="w-4 h-4 ml-2" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Contact */}
      <section className="py-20 bg-paper border-b border-v2border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-ink mb-6">
            Get in Touch
          </h2>
          <p className="text-xl text-ink-500 mb-8">
            Tell us where you sit &mdash; delivering care, funding it or building on it &mdash; and
            the funders you work with.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4 mb-8">
            <Link
              to="/contact"
              className="inline-flex items-center px-8 py-4 bg-ink text-paper text-lg font-semibold rounded-lg hover:bg-ink-700 transition-colors"
            >
              Start the conversation
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
          </div>
          <div className="text-ink-500">
            <p className="mb-2">440 N. Wolfe Road, Sunnyvale, CA 94085</p>
            <p className="mb-2">(800) 823-1470</p>
            <p>support@weavein.ai</p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <MarketingFooter />
    </div>
  );
}
